import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation } from 'react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { enableTooltip } from '../../../../store/tooltip/actions';
import {
  generateDoc,
  generateDocStatus,
  generateWarrantyService,
} from '../../../../services/generate-doc';
import {
  servicesEnum as servicesEnumOBJ,
  warrantyOptions,
  updateServicesStatus,
} from './servicesEnum.js';

const defaultData = {
  // Modal states
  isModalOpen: false,
  modalStep: 1, // 1: selection, 2: warranty selection, 3: confirmation, 4: success
  selectedModalServices: [],
  selectedWarrantyOption: null,
  servicesWithUpdatedValues: [],

  // Main component states
  isMobile: false,
  respondeCreatDoc: null,
  docStatus: null,
  docStatusLoading: false,
  docStatusError: null,
  selectedServices: [],

  // Mutation states
  isConfirmingServices: false,

  // Service availability states
  hasAvailableServices: false,

  // Actions
  setModalOpen: () => {},
  setModalStep: () => {},
  setSelectedModalServices: () => {},
  setSelectedWarrantyOption: () => {},
  setServicesWithUpdatedValues: () => {},
  setIsMobile: () => {},
  setRespondeCreatedDoc: () => {},
  setSelectedServices: () => {},

  // Service handlers
  handleServiceSelection: () => {},
  handleModalServiceSelection: () => {},
  handleWarrantyOptionSelection: () => {},
  handleModalClose: () => {},
  handleProceedToWarranty: () => {},
  handleWarrantyConfirmation: () => {},
  handleConfirmServices: () => {},
  handleBackFromConfirmation: () => {},

  // Utility functions
  getTotalPrice: () => 0,
  fetchData: () => {},
  refetchDocStatus: () => {},
};

export const AdditionalServicesContext = createContext(defaultData);
export const useAdditionalServicesContext = () => useContext(AdditionalServicesContext);
export const AdditionalServicesConsumer = AdditionalServicesContext.Consumer;

export const AdditionalServicesProvider = ({
  children,
  inspectionId,
  isEligibleForWarranty = false,
  isPaidOrEligible = false,
  onConfirmServices: externalOnConfirmServices,
}) => {
  const dispatch = useDispatch();
  const warrantyServiceEnabled = useFlag('mostrar-servico-garantia');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [selectedModalServices, setSelectedModalServices] = useState([]);
  const [selectedWarrantyOption, setSelectedWarrantyOption] = useState(null);
  const [servicesWithUpdatedValues, setServicesWithUpdatedValues] = useState([]);
  // Use useMemo para recalcular quando as dependências mudarem
  const servicesEnum = useMemo(() => {
    const filteredServices = servicesEnumOBJ.filter(service => {
      // Se o serviço de garantia está desabilitado por feature flag, remove completamente
      if (service.key === '02' && !warrantyServiceEnabled) {
        return false;
      }

      // Se não é elegível para garantia, remove o serviço de garantia
      if (service.key === '02' && !isEligibleForWarranty) {
        return false;
      }

      // Se o laudo total não pode ser gerado (não foi pago), remove o serviço
      if (service.key === '04' && !isPaidOrEligible) {
        return false;
      }

      return true;
    });
    return filteredServices;
  }, [isEligibleForWarranty, isPaidOrEligible, warrantyServiceEnabled]);
  // Main component states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 760);
  const [respondeCreatDoc, setRespondeCreatedDoc] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);

  // React Query for doc status with cache
  const {
    data: docStatus,
    isLoading: docStatusLoading,
    error: docStatusError,
    refetch: refetchDocStatus,
  } = useQuery(['docStatus', inspectionId], () => generateDocStatus(inspectionId), {
    enabled: !!inspectionId,
    staleTime: 0, // Sempre considera os dados como stale para permitir refetch
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true, // Refetch quando a janela ganha foco
    refetchOnMount: true, // Refetch quando o componente é montado
    onError: error => {
      console.error('Erro ao buscar status do documento:', error);
    },
  });

  // React Query mutation for document generation
  const generateDocMutation = useMutation(
    ({ inspectionId, reportType }) => generateDoc(inspectionId, reportType),
    {
      onSuccess: (data, variables) => {
        // todo
      },
      onError: (error, variables) => {
        dispatch(enableTooltip({ messageId: 14 }));
      },
    }
  );

  // Fetch data function
  const fetchData = async type => {
    try {
      const response = await generateDoc(inspectionId, type);
      setRespondeCreatedDoc(response);
      dispatch(enableTooltip({ messageId: 13 }));
    } catch (error) {
      console.error(error);
      dispatch(enableTooltip({ messageId: 14 }));
    }
  };

  // Processa o docStatus e atualiza os serviços
  const servicesWithStatus = useMemo(() => {
    const result = updateServicesStatus(servicesEnum, docStatus, isEligibleForWarranty);

    return result;
  }, [servicesEnum, docStatus, warrantyServiceEnabled, isEligibleForWarranty]);

  // Verifica se há serviços com status 'waiting' para determinar o refetchInterval
  const hasWaitingServices = useMemo(() => {
    return servicesWithStatus.some(service => service.status === 'waiting');
  }, [servicesWithStatus]);

  // Verifica se há serviços com status 'offer' disponíveis para solicitação
  const hasAvailableServices = useMemo(() => {
    const available = servicesWithStatus.some(
      service => service.status === 'offer' && service.showCard
    );

    return available;
  }, [servicesWithStatus]);

  // Ref para controlar o interval de refetch
  const refetchIntervalRef = useRef(null);

  // Ref para controlar o throttle das requisições
  const lastRefetchTimeRef = useRef(0);
  const throttleDelay = 5000; // 5 segundos

  // Função throttle para controlar as requisições
  const throttledRefetch = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRefetch = now - lastRefetchTimeRef.current;

    if (timeSinceLastRefetch >= throttleDelay) {
      lastRefetchTimeRef.current = now;
      if (refetchDocStatus) {
        refetchDocStatus();
      }
    }
  }, [refetchDocStatus, throttleDelay]);

  // Controla o refetch interval baseado no status dos serviços
  useEffect(() => {
    // Limpa o interval anterior se existir
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current);
      refetchIntervalRef.current = null;
    }

    // Se há serviços waiting, inicia o refetch a cada 8 segundos
    if (hasWaitingServices && refetchDocStatus) {
      refetchIntervalRef.current = setInterval(() => {
        throttledRefetch();
      }, 8 * 1000); // 8 segundos
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current);
        refetchIntervalRef.current = null;
      }
    };
  }, [hasWaitingServices, throttledRefetch]);

  // Refetch ao abrir modal
  useEffect(() => {
    if (isModalOpen) {
      throttledRefetch();
    }
  }, [isModalOpen, throttledRefetch]);

  // Refetch ao montar componente (voltar da página)
  useEffect(() => {
    throttledRefetch();
  }, [throttledRefetch]);

  // Service selection handler
  const handleServiceSelection = serviceKey => {
    const service = servicesWithStatus.find(s => s.key === serviceKey);

    // Só permite seleção de cards com status 'offer'
    if (service && service.status === 'offer') {
      setSelectedServices(prev => {
        if (prev.includes(serviceKey)) {
          // Remove se já estiver selecionado
          return prev.filter(key => key !== serviceKey);
        } else {
          // Adiciona se não estiver selecionado
          return [...prev, serviceKey];
        }
      });
    }
  };

  // Modal service selection handler
  const handleModalServiceSelection = serviceKey => {
    if (selectedModalServices.includes(serviceKey)) {
      setSelectedModalServices(selectedModalServices.filter(key => key !== serviceKey));
    } else {
      setSelectedModalServices([...selectedModalServices, serviceKey]);
    }
  };

  // Warranty option selection handler
  const handleWarrantyOptionSelection = warrantyKey => {
    setSelectedWarrantyOption(warrantyKey);
  };

  // Get total price function
  const getTotalPrice = () => {
    return selectedModalServices.reduce((total, serviceKey) => {
      const service =
        servicesWithUpdatedValues.find(s => s.key === serviceKey) ||
        servicesWithStatus.find(s => s.key === serviceKey);
      return total + (service ? service.value : 0);
    }, 0);
  };

  // Modal close handler
  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalStep(1);
    setSelectedModalServices([]);
    setSelectedWarrantyOption(null);
    setServicesWithUpdatedValues([]);

    // Refetch status ao fechar modal para manter dados atualizados
    throttledRefetch();
  };

  // Proceed to warranty handler
  const handleProceedToWarranty = () => {
    // Check if warranty is selected and has options
    const warrantyService = servicesWithStatus.find(
      s => s.key === '02' && selectedModalServices.includes('02')
    );
    if (warrantyService && warrantyService.hasOptions) {
      setModalStep(2); // Go to warranty selection
    } else {
      setModalStep(3); // Go directly to confirmation
    }
  };

  // Warranty confirmation handler
  const handleWarrantyConfirmation = () => {
    if (selectedWarrantyOption) {
      // Update the warranty service with the selected option
      const updatedServices = servicesWithStatus.map(service => {
        if (service.key === '02') {
          const selectedOption = warrantyOptions.find(
            option => option.key === selectedWarrantyOption
          );
          return {
            ...service,
            value: selectedOption.price,
            valueText: undefined, // Remove "à partir de" when specific option is selected
            selectedWarrantyOption: selectedOption,
          };
        }
        return service;
      });
      setServicesWithUpdatedValues(updatedServices);
    }
    setModalStep(3); // Go to confirmation
  };

  // Confirm services handler
  const handleConfirmServices = async () => {
    try {
      // Verifica se há serviços selecionados
      if (!selectedModalServices || selectedModalServices.length === 0) {
        setModalStep(4);
        return;
      }

      // Solicita geração de documentos para cada serviço selecionado usando o mutation
      const generationPromises = selectedModalServices.map(async serviceKey => {
        const service = servicesWithStatus.find(s => s.key === serviceKey);

        // Tratamento especial para garantia (key '02')
        if (serviceKey === '02') {
          try {
            // Buscar o serviço da garantia com as opções selecionadas
            const warrantyServiceData =
              servicesWithUpdatedValues.find(s => s.key === '02') || service;

            const warrantyData = {
              serviceKey: serviceKey,
              serviceTitle: warrantyServiceData.title,
              selectedWarrantyOption: warrantyServiceData.selectedWarrantyOption,
              value: warrantyServiceData.value,
              inspectionId: inspectionId,
            };

            const response = await generateWarrantyService(inspectionId, warrantyData);
            return { serviceKey, success: true, response, isWarranty: true };
          } catch (error) {
            return { serviceKey, success: false, error, isWarranty: true };
          }
        }

        if (service && service.reportType) {
          try {
            const response = await generateDocMutation.mutateAsync({
              inspectionId,
              reportType: service.reportType,
            });

            return { serviceKey, success: true, response };
          } catch (error) {
            return { serviceKey, success: false, error };
          }
        } else {
          return { serviceKey, success: true, message: 'No document generation needed' };
        }
      });

      // Aguarda todas as solicitações
      const results = await Promise.all(generationPromises);

      // Atualiza o status dos serviços para 'waiting' imediatamente
      // Mas só se o serviço não estiver 'ready'
      const updatedServices = servicesWithStatus.map(service => {
        if (
          selectedModalServices.includes(service.key) &&
          service.reportType &&
          service.status !== 'ready'
        ) {
          return {
            ...service,
            status: 'waiting',
          };
        }
        return service;
      });

      setServicesEnum(updatedServices);

      // Chama callback externo se existir
      if (externalOnConfirmServices) {
        const finalServices =
          servicesWithUpdatedValues.length > 0 ? servicesWithUpdatedValues : updatedServices;
        externalOnConfirmServices(selectedModalServices, finalServices);
      }

      setModalStep(4);
    } catch (error) {
      // Em caso de erro, ainda fecha o modal
      setModalStep(4);
    }
  };

  // Back from confirmation handler
  const handleBackFromConfirmation = () => {
    // Check if warranty is selected and has options
    const warrantyService = servicesWithStatus.find(
      s => s.key === '02' && selectedModalServices.includes('02')
    );
    if (warrantyService && warrantyService.hasOptions && selectedWarrantyOption) {
      setModalStep(2); // Go back to warranty selection
    } else {
      setModalStep(1); // Go back to service selection
    }
  };

  // Initialize modal services when modal opens
  useEffect(() => {
    if (isModalOpen && selectedServices.length > 0) {
      setSelectedModalServices(selectedServices);
    }
  }, [isModalOpen, selectedServices]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isMobile && window.innerWidth <= 760) {
        return;
      }

      if (!isMobile && window.innerWidth > 760) {
        return;
      }

      if (isMobile && window.innerWidth > 760) {
        setIsMobile(false);
      }

      if (!isMobile && window.innerWidth <= 760) {
        setIsMobile(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  return (
    <AdditionalServicesContext.Provider
      value={{
        // Modal states
        isModalOpen,
        modalStep,
        selectedModalServices,
        selectedWarrantyOption,
        servicesWithUpdatedValues,

        // Main component states
        isMobile,
        respondeCreatDoc,
        docStatus,
        docStatusLoading,
        docStatusError,
        selectedServices,
        servicesEnum,
        servicesWithStatus,

        // Mutation states
        isConfirmingServices: generateDocMutation.isLoading,

        // Service availability states
        hasAvailableServices,

        // Actions
        setModalOpen: setIsModalOpen,
        setModalStep,
        setSelectedModalServices,
        setSelectedWarrantyOption,
        setServicesWithUpdatedValues,
        setIsMobile,
        setRespondeCreatedDoc,
        setSelectedServices,

        // Service handlers
        handleServiceSelection,
        handleModalServiceSelection,
        handleWarrantyOptionSelection,
        handleModalClose,
        handleProceedToWarranty,
        handleWarrantyConfirmation,
        handleConfirmServices,
        handleBackFromConfirmation,

        // Utility functions
        getTotalPrice,
        fetchData,
        refetchDocStatus,
      }}
    >
      {children}
    </AdditionalServicesContext.Provider>
  );
};
